import { supabase } from './supabase';
import { logger } from '../utils/logger';
import { Profile, UserGame, Availability } from '../types/profile';

// Cache for compatibility scores with 5-minute TTL
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const compatibilityCache = new Map<string, CacheEntry<MatchScore>>();
const distanceCache = new Map<string, number>();

function getCacheKey(userId1: string, userId2: string): string {
  // Ensure consistent ordering for bidirectional cache
  return userId1 < userId2 ? `${userId1}:${userId2}` : `${userId2}:${userId1}`;
}

function isCacheValid<T>(entry: CacheEntry<T> | undefined): boolean {
  return !!entry && (Date.now() - entry.timestamp) < CACHE_TTL;
}

/**
 * Clear all matchmaking caches
 */
export function clearMatchmakingCache() {
  compatibilityCache.clear();
  distanceCache.clear();
}

/**
 * Check if one user can see another based on preferences
 */
function canSee(
  myPreferences: string[] | null,
  myGenders: string[] | null,
  theirGenders: string[] | null,
  theirPreferences: string[] | null
): boolean {
  if ((theirPreferences && theirPreferences.length > 0) &&
      (!myGenders || myGenders.length === 0)) {
    return false;
  }

  if (!myPreferences || myPreferences.length === 0) {
    return true;
  }

  if (!theirGenders || theirGenders.length === 0) {
    return false;
  }

  return theirGenders.every(gender => myPreferences.includes(gender));
}

/**
 * Check if two users match based on gender preferences
 */
function checkGenderMatch(
  user1Genders: string[] | null,
  user1Preferences: string[] | null,
  user2Genders: string[] | null,
  user2Preferences: string[] | null
): boolean {
  const user1CanSeeUser2 = canSee(user1Preferences, user1Genders, user2Genders, user2Preferences);
  const user2CanSeeUser1 = canSee(user2Preferences, user2Genders, user1Genders, user1Preferences);
  return user1CanSeeUser2 && user2CanSeeUser1;
}

/**
 * Calculate play preference compatibility score (0-100)
 */
function calculatePlayPreferenceScore(
  myPreferences: string[] | null,
  myPlatforms: string[] | null,
  theirPreferences: string[] | null,
  theirPlatforms: string[] | null
): number {
  // Both have no preferences: neutral score
  if (!myPreferences?.length && !theirPreferences?.length) {
    return 50;
  }

  const myPrefs = myPreferences || [];
  const theirPrefs = theirPreferences || [];
  const overlappingPrefs = myPrefs.filter(p => theirPrefs.includes(p));

  // No overlapping preferences: still give a small baseline if both have set preferences
  if (overlappingPrefs.length === 0) {
    if (myPrefs.length > 0 && theirPrefs.length > 0) {
      return 20; // Both are clear about preferences (even if different)
    }
    return 0;
  }

  // Has overlapping preferences: start with 70
  let score = 70;

  // Bonus: Both prefer the same single mode
  if (myPrefs.length === 1 && theirPrefs.length === 1 && myPrefs[0] === theirPrefs[0]) {
    score += 20;
  }

  // Bonus: Both are flexible (have 2 preferences and they overlap)
  if (myPrefs.length === 2 && theirPrefs.length === 2 && overlappingPrefs.length === 2) {
    score += 15; // Perfect flexibility match
  } else if (myPrefs.length === 2 && theirPrefs.length === 2) {
    score += 5; // Both flexible but only partial overlap
  }

  // OPTIONAL platform matching (for online players)
  if (overlappingPrefs.includes('online')) {
    const myPlats = myPlatforms || [];
    const theirPlats = theirPlatforms || [];

    if (myPlats.length > 0 && theirPlats.length > 0) {
      const sharedPlatforms = myPlats.filter(p => theirPlats.includes(p));

      if (sharedPlatforms.length > 0) {
        score += Math.min(10, sharedPlatforms.length * 3);
      } else {
        score -= 5;
      }
    }
  }

  return Math.max(0, Math.min(100, score));
}

/**
 * Determine if distance should be strictly enforced
 */
function requiresProximity(
  myPreferences: string[] | null,
  theirPreferences: string[] | null
): boolean {
  const myPrefs = myPreferences || [];
  const theirPrefs = theirPreferences || [];

  const myOnlyInPerson = myPrefs.length === 1 && myPrefs[0] === 'in-person';
  const theirOnlyInPerson = theirPrefs.length === 1 && theirPrefs[0] === 'in-person';

  return myOnlyInPerson || theirOnlyInPerson;
}

/**
 * Calculate location score with online play consideration
 */
function calculateLocationScore(
  distance: number,
  maxDistance: number,
  myPreferences: string[] | null,
  theirPreferences: string[] | null
): number {
  const myPrefs = myPreferences || [];
  const theirPrefs = theirPreferences || [];

  const myOnlineOnly = myPrefs.length === 1 && myPrefs[0] === 'online';
  const theirOnlineOnly = theirPrefs.length === 1 && theirPrefs[0] === 'online';
  const bothOnlineOnly = myOnlineOnly && theirOnlineOnly;

  const myIncludesOnline = myPrefs.includes('online');
  const theirIncludesOnline = theirPrefs.includes('online');
  const bothIncludeOnline = myIncludesOnline && theirIncludesOnline;

  if (bothOnlineOnly) {
    return Math.max(75, 100 - (distance / 1000));
  } else if (bothIncludeOnline && !requiresProximity(myPreferences, theirPreferences)) {
    return Math.max(60, 100 - (distance / maxDistance) * 40);
  } else {
    return Math.max(0, (1 - (distance / maxDistance)) * 100);
  }
}

/**
 * Calculate distance between two points (memoized)
 */
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const cacheKey = `${lat1.toFixed(4)},${lon1.toFixed(4)},${lat2.toFixed(4)},${lon2.toFixed(4)}`;
  const cached = distanceCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  distanceCache.set(cacheKey, distance);
  return distance;
}

/**
 * Find games shared between two users (optimized with Sets)
 */
function findSharedGames(myGames: any[], theirGames: any[]): any[] {
  const shared: any[] = [];

  // Create Sets for O(1) lookup instead of O(n) nested loops
  const theirGameIds = new Set<number>();
  const theirCustomGames = new Map<string, any>(); // normalized name -> game

  for (const game of theirGames) {
    const gameId = game?.game_id || 0;

    if (gameId === 0) {
      const name = game?.name?.toLowerCase().trim();
      if (name) {
        theirCustomGames.set(name, game);
      }
    } else {
      theirGameIds.add(Number(gameId));
    }
  }

  // Now check my games against their sets
  const seenGames = new Set<number | string>();

  for (const myGame of myGames) {
    const myGameId = myGame?.game_id || 0;

    if (myGameId === 0) {
      // Custom game - check by name
      const myName = myGame?.name?.toLowerCase().trim();
      if (myName && theirCustomGames.has(myName) && !seenGames.has(myName)) {
        shared.push(theirCustomGames.get(myName));
        seenGames.add(myName);
      }
    } else {
      // BGG game - check by ID
      if (theirGameIds.has(myGameId) && !seenGames.has(myGameId)) {
        // Find the actual game object from theirGames
        const matchingGame = theirGames.find(g => (g?.game_id || 0) === myGameId);
        if (matchingGame) {
          shared.push(matchingGame);
          seenGames.add(typeof myGameId === 'string' ? myGameId : Number(myGameId));
        }
      }
    }
  }

  return shared;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function findOverlappingAvailability(myAvailability: Availability[], theirAvailability: Availability[]): Availability[] {
  const overlaps: Availability[] = [];

  for (const mySlot of myAvailability) {
    for (const theirSlot of theirAvailability) {
      if (mySlot.day_of_week !== theirSlot.day_of_week) continue;

      const myStart = timeToMinutes(mySlot.start_time);
      const myEnd = timeToMinutes(mySlot.end_time);
      const theirStart = timeToMinutes(theirSlot.start_time);
      const theirEnd = timeToMinutes(theirSlot.end_time);

      const overlapStart = Math.max(myStart, theirStart);
      const overlapEnd = Math.min(myEnd, theirEnd);

      if (overlapStart < overlapEnd) {
        const startHours = Math.floor(overlapStart / 60);
        const startMinutes = overlapStart % 60;
        const endHours = Math.floor(overlapEnd / 60);
        const endMinutes = overlapEnd % 60;

        overlaps.push({
          day_of_week: mySlot.day_of_week,
          start_time: `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`,
          end_time: `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`,
          user_id: mySlot.user_id
        });
      }
    }
  }

  return overlaps;
}

function calculateCompatibilityScore(
  myGames: any[],
  theirGames: any[],
  myAvailability: Availability[],
  theirAvailability: Availability[],
  distance: number,
  maxDistance: number,
  myPlayPreferences: string[] | null,
  myPlatforms: string[] | null,
  theirPlayPreferences: string[] | null,
  theirPlatforms: string[] | null
): { score: number; breakdown: { games: number; availability: number; location: number; playStyle: number } } {
  const sharedGames = findSharedGames(myGames, theirGames);
  const overlappingSlots = findOverlappingAvailability(myAvailability, theirAvailability);

  let gamesScore = 0;
  if (sharedGames.length > 0) {
    // Has shared games: 70-100
    gamesScore = Math.min(100, 70 + (sharedGames.length * 10));
  } else if (myGames.length > 0 && theirGames.length > 0) {
    // No shared games but both have games: give baseline 30-40
    const avgGames = (myGames.length + theirGames.length) / 2;
    gamesScore = Math.min(40, 30 + avgGames);
  }

  let availabilityScore = 0;
  if (overlappingSlots.length > 0) {
    // Has overlapping availability: 50-100
    const totalOverlapMinutes = overlappingSlots.reduce((sum, slot) => {
      const start = timeToMinutes(slot.start_time);
      const end = timeToMinutes(slot.end_time);
      return sum + (end - start);
    }, 0);

    const overlapHours = totalOverlapMinutes / 60;
    availabilityScore = Math.min(100, 50 + (overlapHours * 12));
  } else if (myAvailability.length > 0 && theirAvailability.length > 0) {
    // No overlap but both have availability: give baseline 30
    availabilityScore = 30;
  }

  const locationScore = calculateLocationScore(
    distance,
    maxDistance,
    myPlayPreferences,
    theirPlayPreferences
  );

  const playStyleScore = calculatePlayPreferenceScore(
    myPlayPreferences,
    myPlatforms,
    theirPlayPreferences,
    theirPlatforms
  );

  // Rebalanced weights: more emphasis on location and play style
  const finalScore = (
    gamesScore * 0.35 +       // 35% (down from 45%)
    availabilityScore * 0.25 + // 25% (down from 30%)
    locationScore * 0.25 +     // 25% (up from 15%)
    playStyleScore * 0.15      // 15% (up from 10%)
  );

  return {
    score: finalScore,
    breakdown: {
      games: gamesScore,
      availability: availabilityScore,
      location: locationScore,
      playStyle: playStyleScore
    }
  };
}

async function findMatches(): Promise<{ success: boolean; error?: string; matches: MatchScore[] }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated', matches: [] };
    }

    // Fetch my profile
    const { data: myProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (!myProfile) {
      return { success: false, error: 'Profile not found', matches: [] };
    }

    // Batch fetch my data in parallel
    const [
      { data: myGames },
      { data: myAvailability },
      { data: existingMatches },
      { data: blockedByMe },
      { data: blockedMe }
    ] = await Promise.all([
      supabase
        .from('user_games')
        .select('*')
        .eq('user_id', user.id),
      supabase
        .from('availability')
        .select('*')
        .eq('user_id', user.id),
      supabase
        .from('matches')
        .select('user1_id, user2_id, status, updated_at, initiated_by')
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`),
      supabase
        .from('blocked_users')
        .select('blocked_id')
        .eq('blocker_id', user.id),
      supabase
        .from('blocked_users')
        .select('blocker_id')
        .eq('blocked_id', user.id)
    ]);

    if (!myGames || !myAvailability) {
      return { success: false, error: 'Profile incomplete', matches: [] };
    }

    // Build exclusion set
    const excludedUserIds = new Set<string>([user.id]);

    // Exclude users based on match status:
    // - Always exclude accepted and pending connections
    // - For rejected matches, distinguish between three cases:
    //   1. You CANCELLED your own pending request → don't exclude (you can change your mind)
    //   2. You REJECTED their request → exclude temporarily (24h cooldown for accidental rejections)
    //   3. They REJECTED your request → exclude permanently from auto-match
    //      (prevents awkwardness, but they can still find you via "find a friend")
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    existingMatches?.forEach(match => {
      const otherUserId = match.user1_id === user.id ? match.user2_id : match.user1_id;

      if (match.status === 'accepted' || match.status === 'pending') {
        // Always exclude active connections
        excludedUserIds.add(otherUserId);
      } else if (match.status === 'rejected') {
        if (match.initiated_by === user.id) {
          // You sent a request and they rejected it
          // Permanently exclude from auto-match queue (prevents awkwardness)
          // They can still manually find you via "find a friend" after 24 hours
          excludedUserIds.add(otherUserId);
        } else {
          // They sent a request and you rejected it
          // Temporarily exclude for 24 hours in case of accidental rejection
          const updatedAt = new Date(match.updated_at);
          if (updatedAt > twentyFourHoursAgo) {
            excludedUserIds.add(otherUserId);
          }
        }
      }
    });

    // Always exclude blocked users
    blockedByMe?.forEach(b => excludedUserIds.add(b.blocked_id));
    blockedMe?.forEach(b => excludedUserIds.add(b.blocker_id));

    // Fetch other profiles
    const { data: otherProfiles } = await supabase
      .from('profiles')
      .select('*')
      .neq('id', user.id)
      .not('location_lat', 'is', null)
      .not('location_lng', 'is', null);

    if (!otherProfiles || otherProfiles.length === 0) {
      return { success: true, matches: [] };
    }

    // Filter by gender preferences first (cheap operation)
    const genderFilteredProfiles = otherProfiles.filter(profile => {
      if (excludedUserIds.has(profile.id)) return false;

      return checkGenderMatch(
        myProfile.gender as string[] | null,
        myProfile.gender_preference as string[] | null,
        profile.gender as string[] | null,
        profile.gender_preference as string[] | null
      );
    });

    if (genderFilteredProfiles.length === 0) {
      return { success: true, matches: [] };
    }

    // Batch fetch ALL games and availability for filtered profiles in parallel
    const profileIds = genderFilteredProfiles.map(p => p.id);

    const [
      { data: allUserGames },
      { data: allUserAvailability }
    ] = await Promise.all([
      supabase
        .from('user_games')
        .select('*')
        .in('user_id', profileIds),
      supabase
        .from('availability')
        .select('*')
        .in('user_id', profileIds)
    ]);

    // Organize by user ID for fast lookup
    const gamesByUserId = new Map<string, any[]>();
    const availabilityByUserId = new Map<string, Availability[]>();

    allUserGames?.forEach(game => {
      if (!gamesByUserId.has(game.user_id)) {
        gamesByUserId.set(game.user_id, []);
      }
      gamesByUserId.get(game.user_id)!.push(game);
    });

    allUserAvailability?.forEach(slot => {
      if (!availabilityByUserId.has(slot.user_id)) {
        availabilityByUserId.set(slot.user_id, []);
      }
      availabilityByUserId.get(slot.user_id)!.push(slot);
    });

    // Calculate compatibility scores for all profiles
    const matches: MatchScore[] = [];

    for (const profile of genderFilteredProfiles) {
      // Check cache first
      const cacheKey = getCacheKey(user.id, profile.id);
      const cached = compatibilityCache.get(cacheKey);

      if (isCacheValid(cached)) {
        matches.push(cached!.data);
        continue;
      }

      // Check if both profiles have valid location data
      const hasLocationData =
        myProfile.location_lat != null &&
        myProfile.location_lng != null &&
        profile.location_lat != null &&
        profile.location_lng != null;

      let distance = 0;
      if (hasLocationData) {
        distance = calculateDistance(
          myProfile.location_lat,
          myProfile.location_lng,
          profile.location_lat,
          profile.location_lng
        );
      } else {
        // Skip profiles without location data unless both prefer online-only
        const myIncludesOnline = myProfile.play_preferences?.includes('online');
        const theirIncludesOnline = profile.play_preferences?.includes('online');
        const bothOnlineOnly =
          myProfile.play_preferences?.length === 1 &&
          myProfile.play_preferences[0] === 'online' &&
          profile.play_preferences?.length === 1 &&
          profile.play_preferences[0] === 'online';

        if (!bothOnlineOnly) {
          continue;
        }
        // For online-only matches, distance is irrelevant
        distance = 0;
      }

      // Check distance requirements
      const proximityRequired = requiresProximity(
        myProfile.play_preferences as string[] | null,
        profile.play_preferences as string[] | null
      );

      const effectiveMaxDistance = proximityRequired
        ? Math.min(myProfile.max_distance_km || 50, profile.max_distance_km || 50)
        : 10000;

      if (distance > effectiveMaxDistance) {
        continue;
      }

      const theirGames = gamesByUserId.get(profile.id) || [];
      const theirAvailability = availabilityByUserId.get(profile.id) || [];

      const sharedGames = findSharedGames(myGames, theirGames);
      const overlappingSlots = findOverlappingAvailability(myAvailability, theirAvailability);

      const compatibilityScore = calculateCompatibilityScore(
        myGames,
        theirGames,
        myAvailability,
        theirAvailability,
        distance,
        Math.min(myProfile.max_distance_km || 50, profile.max_distance_km || 50),
        myProfile.play_preferences as string[] | null,
        myProfile.online_platforms as string[] | null,
        profile.play_preferences as string[] | null,
        profile.online_platforms as string[] | null
      );

      const matchScore: MatchScore = {
        profile,
        distance,
        sharedGames,
        overlappingSlots,
        compatibilityScore: compatibilityScore.score,
        breakdown: compatibilityScore.breakdown
      };

      // Cache the result
      compatibilityCache.set(cacheKey, {
        data: matchScore,
        timestamp: Date.now()
      });

      matches.push(matchScore);
    }

    // Sort by compatibility score
    matches.sort((a, b) => b.compatibilityScore - a.compatibilityScore);

    return { success: true, matches };

  } catch (error) {
    logger.error('Error in matchmaking:', error);
    return { success: false, error: 'Matchmaking failed', matches: [] };
  }
}

export interface MatchScore {
  profile: Profile;
  distance: number;
  sharedGames: UserGame[];
  overlappingSlots: Availability[];
  compatibilityScore: number;
  breakdown: {
    games: number;
    availability: number;
    location: number;
    playStyle: number;
  };
}

export const matchmakingService = {
  findMatches,
  clearMatchmakingCache
};